import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Download, Copy } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { getOutletId, clearOutletId } from "../utils/appNavigation";

export default function AppQR() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get outlet ID from query param or localStorage
  const params = new URLSearchParams(location.search);
  const outletId = getOutletId(params);
  
  const [outlet, setOutlet] = useState(null);
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // No outlet ID - redirect to settings
    if (!outletId) {
      navigate("/app/settings");
      return;
    }

    const fetchData = async () => {
      try {
        const [o, q] = await Promise.all([
          api.get(`/outlets/${outletId}`),
          api.get(`/outlets/${outletId}/qr`),
        ]);
        
        if (!o.data) {
          // Outlet not found - clear and redirect
          clearOutletId();
          navigate("/app/settings");
          return;
        }
        
        setOutlet(o.data);
        setQr(q.data);
      } catch (e) {
        setError("Failed to load QR code");
        toast.error("Could not load outlet data");
      } finally { 
        setLoading(false); 
      }
    };
    
    fetchData();
  }, [outletId, navigate]);

  const publicUrl = outlet ? `${window.location.origin}/r/${outlet.slug}` : "";

  const downloadPNG = () => {
    if (!qr?.image_base64) return;
    const a = document.createElement("a");
    a.href = qr.image_base64;
    a.download = `${outlet?.slug || "qr"}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}>
        <div className="tag">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-5" style={{ background: "var(--bg)" }}>
        <div className="card-pop p-8 text-center max-w-sm">
          <h2 className="display text-xl font-black mb-3">Error</h2>
          <p className="text-[var(--ink-muted)] mb-4">{error}</p>
          <button onClick={() => navigate("/app/settings")} className="btn-primary">
            Go to Settings
          </button>
        </div>
      </div>
    );
  }

  if (!outlet) return null;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      {/* Header */}
      <header className="sticky top-0 z-10 px-5 py-4 flex items-center gap-3" style={{ background: "var(--bg)", borderBottom: "1.5px solid var(--ink)" }}>
        <button
          onClick={handleBack}
          className="w-9 h-9 flex items-center justify-center rounded-lg transition-all hover:opacity-70"
          style={{ border: "1.5px solid var(--ink)" }}
          aria-label="Back"
        >
          <ArrowLeft size={20} weight="bold" />
        </button>
        <h1 className="display text-xl font-black">QR Code</h1>
      </header>

      {/* Main Content */}
      <main className="p-5 max-w-xl mx-auto w-full pb-8">
        <div className="card-pop p-6 flex flex-col items-center" style={{ background: "var(--secondary)" }}>
          <div className="bg-white p-4 rounded-2xl border-[1.5px] border-[var(--ink)]">
            <QRCodeSVG value={publicUrl} size={200} bgColor="#ffffff" fgColor="#1A1A1A" level="M" includeMargin={false} />
          </div>
          <div className="mt-4 text-center">
            <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Scans go to</div>
            <div className="mt-1 text-sm font-semibold break-all max-w-xs">{publicUrl}</div>
          </div>
          <div className="mt-5 flex flex-wrap gap-3 justify-center">
            <button onClick={downloadPNG} className="btn-primary">
              <Download size={16} weight="bold" /> Download PNG
            </button>
            <button onClick={copyLink} className="btn-ghost">
              <Copy size={16} weight="bold" /> Copy link
            </button>
          </div>
        </div>

        <div className="card-pop p-5 mt-5">
          <h3 className="display text-lg font-extrabold">How to use</h3>
          <ol className="mt-3 space-y-2 text-sm">
            <li className="flex gap-3">
              <span className="h-5 w-5 rounded-full text-white flex items-center justify-center font-bold text-xs flex-shrink-0" style={{ background: "var(--primary)" }}>1</span>
              <span>Download the PNG and print on tables or receipts.</span>
            </li>
            <li className="flex gap-3">
              <span className="h-5 w-5 rounded-full text-white flex items-center justify-center font-bold text-xs flex-shrink-0" style={{ background: "var(--primary)" }}>2</span>
              <span>Customers scan with their phone camera.</span>
            </li>
            <li className="flex gap-3">
              <span className="h-5 w-5 rounded-full text-white flex items-center justify-center font-bold text-xs flex-shrink-0" style={{ background: "var(--primary)" }}>3</span>
              <span>They review, then spin for rewards.</span>
            </li>
          </ol>
        </div>
      </main>
    </div>
  );
}